import React from 'react';
import { formatString } from '@deadpan/shared';
import { useTranslations } from '../hooks/useTranslations';
import { getServerHttpBase } from '../utils/serverHttpUrl';

type AttendancePreference = 'confirmed' | 'alternate' | 'skip';
type TimePreference = 'available' | 'skip';

type SignupAttendanceFields = {
  attendance: AttendancePreference;
  alsoFlexible: boolean;
  alternateDates: string[];
  alternateTimes: string[];
};

function deriveSignupAttendance(
  timePreference: TimePreference,
  selectedDefaultSession: boolean,
  selectedDefaultSessionDate: boolean,
  selectedAlternateDates: string[],
  selectedAlternateTimes: string[],
): SignupAttendanceFields | null {
  if (timePreference === 'skip') {
    return {
      attendance: 'skip',
      alsoFlexible: false,
      alternateDates: [],
      alternateTimes: [],
    };
  }

  const hasDefault = selectedDefaultSession && selectedDefaultSessionDate;
  const alternateDates = selectedAlternateDates;
  const alternateTimes = selectedAlternateTimes;
  const hasAlternates = alternateDates.length > 0 || alternateTimes.length > 0;

  if (!hasDefault && !hasAlternates) {
    return null;
  }
  if (hasDefault && hasAlternates) {
    return {
      attendance: 'confirmed',
      alsoFlexible: true,
      alternateDates,
      alternateTimes,
    };
  }
  if (hasDefault) {
    return {
      attendance: 'confirmed',
      alsoFlexible: false,
      alternateDates: [],
      alternateTimes: [],
    };
  }
  return {
    attendance: 'alternate',
    alsoFlexible: false,
    alternateDates,
    alternateTimes,
  };
}

type SignupOk = {
  ok: true;
  sessionDate: string;
  sessionHourPst?: number;
  sessionMinutePst?: number;
  alreadyRegistered: string[];
  newlyRegistered: string[];
  newlyConfirmed?: string[];
  newlyWaitlisted?: string[];
  confirmedCount?: number;
  roomCapacity?: number;
  roomCount?: number;
};

type SignupErr = { ok: false; error: string };

type SignupResponse = SignupOk | SignupErr;
type ConfigResponse =
  | {
      ok: true;
      sessionDate: string;
      sessionHourPst: number;
      sessionMinutePst?: number;
      theme?: string;
      defaultTheme?: string;
      optionalThemes?: string[];
      defaultThemeRequired?: boolean;
      alternateDateOptions?: string[];
      alternateTimeOptions?: string[];
      confirmedCount?: number;
      roomCapacity?: number;
      roomCount?: number;
    }
  | SignupErr;

const ALT_DATE_LABEL_KEYS = {
  sat: 'weekendHahaAltDate_sat',
  sun: 'weekendHahaAltDate_sun',
  fri: 'weekendHahaAltDate_fri',
} as const;

function parseSessionDateParts(sessionDate: string): { month: number; day: number; year: number } | null {
  const match = sessionDate.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return null;
  return {
    month: Number(match[1]),
    day: Number(match[2]),
    year: Number(match[3]),
  };
}

function formatClock(hour24: number, minute = 0): string {
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const ampm = hour24 >= 12 ? 'pm' : 'am';
  return `${hour12}:${String(minute).padStart(2, '0')}${ampm}`;
}

const DISPLAY_TIME_RE = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/iu;

function parseDisplayTimeToMinutes(raw: string): number | null {
  const match = raw.trim().match(DISPLAY_TIME_RE);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = match[2] != null ? Number(match[2]) : 0;
  const meridiem = match[3].toLowerCase();
  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;
  if (meridiem === 'pm' && hour !== 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;
  return hour * 60 + minute;
}

type TimeChipEntry =
  | { kind: 'default'; label: string; sortKey: number }
  | { kind: 'alternate'; time: string; label: string; sortKey: number };

function buildSortedTimeChips(
  defaultSessionLabel: string,
  alternateTimeOptions: string[],
): TimeChipEntry[] {
  const entries: TimeChipEntry[] = [];
  if (defaultSessionLabel !== '') {
    const sortKey = parseDisplayTimeToMinutes(defaultSessionLabel);
    if (sortKey != null) {
      entries.push({ kind: 'default', label: defaultSessionLabel, sortKey });
    }
  }
  for (const time of alternateTimeOptions) {
    const sortKey = parseDisplayTimeToMinutes(time);
    if (sortKey != null) {
      entries.push({ kind: 'alternate', time, label: time, sortKey });
    }
  }
  return entries.sort((a, b) => a.sortKey - b.sortKey);
}

const PACIFIC_TIME_ZONE = 'America/Los_Angeles';
const BEIJING_TIME_ZONE = 'Asia/Shanghai';

function getTimeZoneParts(
  instant: Date,
  timeZone: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(instant);
  const lookup = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '0';
  return {
    year: Number(lookup('year')),
    month: Number(lookup('month')),
    day: Number(lookup('day')),
    hour: Number(lookup('hour')) % 24,
    minute: Number(lookup('minute')),
    second: Number(lookup('second')),
  };
}

function wallClockToUtcInstant(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  let instantMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 5; i++) {
    const parts = getTimeZoneParts(new Date(instantMs), timeZone);
    const desiredMs = Date.UTC(year, month - 1, day, hour, minute, 0);
    const actualMs = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    const diffMs = desiredMs - actualMs;
    if (diffMs === 0) break;
    instantMs += diffMs;
  }
  return new Date(instantMs);
}

function formatInstantAsDateAndClock(
  instant: Date,
  timeZone: string,
): { dateMd: string; hour24: number; minute: number } {
  const parts = getTimeZoneParts(instant, timeZone);
  return {
    dateMd: `${String(parts.month).padStart(2, '0')}/${String(parts.day).padStart(2, '0')}`,
    hour24: parts.hour,
    minute: parts.minute,
  };
}

function formatSessionDateMd(sessionDate: string): string {
  return sessionDate.slice(0, 5).replace('-', '/');
}

// Session date/time are US Pacific wall-clock values (PST/PDT via America/Los_Angeles).
function toBeijingFromPacific(
  sessionDate: string,
  hourPacific: number,
  minutePacific = 0,
): string | null {
  const p = parseSessionDateParts(sessionDate);
  if (!p) return null;
  const instant = wallClockToUtcInstant(
    p.year,
    p.month,
    p.day,
    hourPacific,
    minutePacific,
    PACIFIC_TIME_ZONE,
  );
  const { dateMd, hour24, minute } = formatInstantAsDateAndClock(instant, BEIJING_TIME_ZONE);
  return `${dateMd} ${formatClock(hour24, minute)}`;
}

function formatSessionTimeLine(
  sessionDate: string,
  hourPacific: number,
  minutePacific = 0,
): string {
  const pt = `${formatSessionDateMd(sessionDate)} ${formatClock(hourPacific, minutePacific)}`;
  const bj = toBeijingFromPacific(sessionDate, hourPacific, minutePacific) ?? '--';
  return `${pt} / ${bj}`;
}

const ALT_DATE_SORT_ORDER: Record<string, number> = { fri: 0, sat: 1, sun: 2 };

const PACIFIC_WEEKDAY_TO_KEY: Record<string, string> = {
  Friday: 'fri',
  Saturday: 'sat',
  Sunday: 'sun',
};

function sessionDateToPacificDayKey(sessionDate: string): string | null {
  const p = parseSessionDateParts(sessionDate);
  if (!p) return null;
  const instant = wallClockToUtcInstant(p.year, p.month, p.day, 12, 0, PACIFIC_TIME_ZONE);
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    weekday: 'long',
  }).format(instant);
  return PACIFIC_WEEKDAY_TO_KEY[weekday] ?? null;
}

type DateChipEntry =
  | { kind: 'default'; key: string; label: string; sortKey: number }
  | { kind: 'alternate'; key: string; label: string; sortKey: number };

function buildSortedDateChips(
  defaultDateKey: string | null,
  alternateDateOptions: string[],
  altDateLabel: (key: string) => string,
): DateChipEntry[] {
  const entries: DateChipEntry[] = [];
  const seen = new Set<string>();
  if (defaultDateKey) {
    entries.push({
      kind: 'default',
      key: defaultDateKey,
      label: altDateLabel(defaultDateKey),
      sortKey: ALT_DATE_SORT_ORDER[defaultDateKey] ?? 99,
    });
    seen.add(defaultDateKey);
  }
  for (const key of alternateDateOptions) {
    if (seen.has(key)) continue;
    entries.push({
      kind: 'alternate',
      key,
      label: altDateLabel(key),
      sortKey: ALT_DATE_SORT_ORDER[key] ?? 99,
    });
  }
  return entries.sort((a, b) => a.sortKey - b.sortKey);
}

function normalizeStringList(raw: unknown, exclude?: string | null): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    if (trimmed === '' || trimmed === exclude || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

function toggleListItem(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter((value) => value !== item) : [...list, item];
}

type OptionChipProps = {
  label: string;
  selected: boolean;
  disabled: boolean;
  locked?: boolean;
  onToggle: () => void;
};

const OptionChip: React.FC<OptionChipProps> = ({
  label,
  selected,
  disabled,
  locked = false,
  onToggle,
}) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={selected}
    aria-disabled={locked}
    aria-label={label}
    disabled={disabled || locked}
    onClick={onToggle}
    className={`min-h-[40px] px-4 py-2 rounded-full border-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
      locked ? 'cursor-default' : 'disabled:cursor-not-allowed'
    } ${
      selected
        ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm'
        : 'bg-white text-gray-800 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
    }`}
  >
    <span className="inline-flex items-center gap-1.5">
      {!locked ? (
        <span aria-hidden="true" className="text-base leading-none">
          {selected ? '☑' : '☐'}
        </span>
      ) : null}
      {label}
    </span>
  </button>
);

type FlexibleTimesChipsBlockProps = {
  timeChips: TimeChipEntry[];
  dateChips: DateChipEntry[];
  selectedDefaultSession: boolean;
  selectedDefaultSessionDate: boolean;
  selectedAlternateDates: string[];
  selectedAlternateTimes: string[];
  loading: boolean;
  datesLabel: string;
  timesLabel: string;
  pstHint: string;
  onToggleDefaultSession: () => void;
  onToggleDefaultSessionDate: () => void;
  onToggleDate: (key: string) => void;
  onToggleTime: (time: string) => void;
};

const FlexibleTimesChipsBlock: React.FC<FlexibleTimesChipsBlockProps> = ({
  timeChips,
  dateChips,
  selectedDefaultSession,
  selectedDefaultSessionDate,
  selectedAlternateDates,
  selectedAlternateTimes,
  loading,
  datesLabel,
  timesLabel,
  pstHint,
  onToggleDefaultSession,
  onToggleDefaultSessionDate,
  onToggleDate,
  onToggleTime,
}) => (
  <div className="space-y-3">
    {dateChips.length > 0 || timeChips.length > 0 ? (
      <p className="text-xs text-gray-600">{pstHint}</p>
    ) : null}
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-800">{timesLabel}</p>
      <div className="flex flex-wrap gap-2">
        {timeChips.map((entry) =>
          entry.kind === 'default' ? (
            <OptionChip
              key="default-session"
              label={entry.label}
              selected={selectedDefaultSession}
              disabled={loading}
              onToggle={onToggleDefaultSession}
            />
          ) : (
            <OptionChip
              key={entry.time}
              label={entry.label}
              selected={selectedAlternateTimes.includes(entry.time)}
              disabled={loading}
              onToggle={() => onToggleTime(entry.time)}
            />
          ),
        )}
      </div>
    </div>

    {dateChips.length > 0 ? (
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-800">{datesLabel}</p>
        <div className="flex flex-wrap gap-2">
          {dateChips.map((entry) =>
            entry.kind === 'default' ? (
              <OptionChip
                key="default-session-date"
                label={entry.label}
                selected={selectedDefaultSessionDate}
                disabled={loading}
                onToggle={onToggleDefaultSessionDate}
              />
            ) : (
              <OptionChip
                key={entry.key}
                label={entry.label}
                selected={selectedAlternateDates.includes(entry.key)}
                disabled={loading}
                onToggle={() => onToggleDate(entry.key)}
              />
            ),
          )}
        </div>
      </div>
    ) : null}
  </div>
);

function applyRoomStatusFromResponse(
  data: {
    confirmedCount?: number;
    roomCapacity?: number;
    roomCount?: number;
  },
  setConfirmedCount: React.Dispatch<React.SetStateAction<number | null>>,
  setRoomCapacity: React.Dispatch<React.SetStateAction<number | null>>,
  setRoomCount: React.Dispatch<React.SetStateAction<number | null>>,
): void {
  setConfirmedCount(typeof data.confirmedCount === 'number' ? data.confirmedCount : null);
  setRoomCapacity(typeof data.roomCapacity === 'number' ? data.roomCapacity : null);
  setRoomCount(typeof data.roomCount === 'number' ? data.roomCount : null);
}

export const WeekendHahaSignup: React.FC = () => {
  const t = useTranslations();
  const [names, setNames] = React.useState('');
  const [note, setNote] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [sessionDate, setSessionDate] = React.useState<string>('');
  const [sessionHourPst, setSessionHourPst] = React.useState<number>(20);
  const [sessionMinutePst, setSessionMinutePst] = React.useState<number>(0);
  const [defaultTheme, setDefaultTheme] = React.useState<string | null>(null);
  const [optionalThemes, setOptionalThemes] = React.useState<string[]>([]);
  const [defaultThemeRequired, setDefaultThemeRequired] = React.useState(false);
  const [selectedThemes, setSelectedThemes] = React.useState<string[]>([]);
  const [alternateDateOptions, setAlternateDateOptions] = React.useState<string[]>([]);
  const [alternateTimeOptions, setAlternateTimeOptions] = React.useState<string[]>([]);
  const [timeSectionOpen, setTimeSectionOpen] = React.useState(false);
  const [timePreference, setTimePreference] = React.useState<TimePreference>('available');
  const [selectedDefaultSession, setSelectedDefaultSession] = React.useState(true);
  const [selectedDefaultSessionDate, setSelectedDefaultSessionDate] = React.useState(true);
  const [selectedAlternateDates, setSelectedAlternateDates] = React.useState<string[]>([]);
  const [selectedAlternateTimes, setSelectedAlternateTimes] = React.useState<string[]>([]);
  const [confirmedCount, setConfirmedCount] = React.useState<number | null>(null);
  const [roomCapacity, setRoomCapacity] = React.useState<number | null>(null);
  const [roomCount, setRoomCount] = React.useState<number | null>(null);

  const allThemes = React.useMemo(() => {
    const out: string[] = [];
    if (defaultTheme) out.push(defaultTheme);
    for (const theme of optionalThemes) {
      if (!out.includes(theme)) out.push(theme);
    }
    return out;
  }, [defaultTheme, optionalThemes]);

  const hasThemeOptions = allThemes.length > 0;
  const sessionTimeText =
    sessionDate !== ''
      ? formatString(t.ui.weekendHahaTimeLine, {
          pt: `${formatSessionDateMd(sessionDate)} ${formatClock(sessionHourPst, sessionMinutePst)}`,
          bj: toBeijingFromPacific(sessionDate, sessionHourPst, sessionMinutePst) ?? '--',
        })
      : '';
  const defaultSessionChipLabel = sessionDate !== '' ? formatClock(sessionHourPst, sessionMinutePst) : '';
  const sortedTimeChips = React.useMemo(
    () => buildSortedTimeChips(defaultSessionChipLabel, alternateTimeOptions),
    [defaultSessionChipLabel, alternateTimeOptions],
  );

  const altDateLabel = (key: string): string => {
    const labelKey = ALT_DATE_LABEL_KEYS[key as keyof typeof ALT_DATE_LABEL_KEYS];
    return labelKey ? t.ui[labelKey] : key;
  };

  const defaultSessionDateKey = sessionDate !== '' ? sessionDateToPacificDayKey(sessionDate) : null;
  const sortedDateChips = React.useMemo(
    () => buildSortedDateChips(defaultSessionDateKey, alternateDateOptions, altDateLabel),
    [defaultSessionDateKey, alternateDateOptions, t],
  );

  const hasAlternateDateOptions = alternateDateOptions.length > 0 || defaultSessionDateKey != null;
  const hasAlternateTimeOptions = alternateTimeOptions.length > 0;

  const alternatePstHint = hasAlternateDateOptions && hasAlternateTimeOptions
    ? t.ui.weekendHahaAlternatePstHint
    : hasAlternateDateOptions
      ? t.ui.weekendHahaAlternatePstHintDates
      : t.ui.weekendHahaAlternatePstHintTimes;

  const formatAlternateSummary = (dates: string[], times: string[]): string => {
    const parts = [...dates.map(altDateLabel), ...times];
    return parts.join('、');
  };

  const setTimePreferenceMode = (value: TimePreference) => {
    setTimePreference(value);
    if (value === 'skip') {
      setSelectedDefaultSession(false);
      setSelectedDefaultSessionDate(false);
      setSelectedAlternateDates([]);
      setSelectedAlternateTimes([]);
    } else {
      setSelectedDefaultSession(true);
      setSelectedDefaultSessionDate(true);
    }
  };

  const resetTimePreferences = () => {
    setTimeSectionOpen(false);
    setTimePreference('available');
    setSelectedDefaultSession(true);
    setSelectedDefaultSessionDate(true);
    setSelectedAlternateDates([]);
    setSelectedAlternateTimes([]);
  };

  const resetThemes = () => {
    setSelectedThemes(defaultTheme ? [defaultTheme] : []);
  };

  const toggleTheme = (theme: string) => {
    if (defaultThemeRequired && theme === defaultTheme) return;
    setSelectedThemes((prev) => toggleListItem(prev, theme));
  };

  React.useEffect(() => {
    if (!defaultThemeRequired || !defaultTheme) return;
    setSelectedThemes((prev) => (prev.includes(defaultTheme) ? prev : [defaultTheme, ...prev]));
  }, [defaultTheme, defaultThemeRequired]);

  React.useEffect(() => {
    let alive = true;
    const loadConfig = async () => {
      try {
        const res = await fetch(`${getServerHttpBase()}/api/weekend-signup/config`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        const data = (await res.json()) as ConfigResponse;
        if (!alive || !res.ok || !data.ok) return;
        setSessionDate(data.sessionDate);
        setSessionHourPst(data.sessionHourPst);
        setSessionMinutePst(typeof data.sessionMinutePst === 'number' ? data.sessionMinutePst : 0);

        const resolvedDefaultRaw =
          typeof data.defaultTheme === 'string' && data.defaultTheme.trim() !== ''
            ? data.defaultTheme.trim()
            : typeof data.theme === 'string' && data.theme.trim() !== ''
              ? data.theme.trim()
              : null;
        const resolvedOptional = normalizeStringList(data.optionalThemes, resolvedDefaultRaw);
        const themeRequired =
          data.defaultThemeRequired === true ||
          (resolvedDefaultRaw != null && resolvedOptional.length === 0);

        setDefaultTheme(resolvedDefaultRaw);
        setOptionalThemes(resolvedOptional);
        setDefaultThemeRequired(themeRequired);
        setSelectedThemes(resolvedDefaultRaw ? [resolvedDefaultRaw] : []);
        setAlternateDateOptions(normalizeStringList(data.alternateDateOptions));
        setAlternateTimeOptions(normalizeStringList(data.alternateTimeOptions));
        applyRoomStatusFromResponse(data, setConfirmedCount, setRoomCapacity, setRoomCount);
      } catch {
        // Keep the form usable even if config fetch fails.
      }
    };
    void loadConfig();
    return () => {
      alive = false;
    };
  }, []);

  const submit = async () => {
    setFeedback(null);
    if (!names.trim()) {
      setFeedback(t.ui.weekendHahaNeedNames);
      return;
    }
    if (hasThemeOptions && selectedThemes.length === 0) {
      setFeedback(t.ui.weekendHahaNeedTheme);
      return;
    }
    if (defaultThemeRequired && defaultTheme && !selectedThemes.includes(defaultTheme)) {
      setFeedback(t.ui.weekendHahaNeedTheme);
      return;
    }
    if (
      timeSectionOpen &&
      timePreference === 'available' &&
      !selectedDefaultSession &&
      !selectedDefaultSessionDate &&
      selectedAlternateDates.length === 0 &&
      selectedAlternateTimes.length === 0
    ) {
      setFeedback(t.ui.weekendHahaNeedAlternate);
      return;
    }
    const signupAttendance = deriveSignupAttendance(
      timePreference,
      selectedDefaultSession,
      selectedDefaultSessionDate,
      selectedAlternateDates,
      selectedAlternateTimes,
    );
    if (!signupAttendance) {
      setFeedback(t.ui.weekendHahaNeedAlternate);
      return;
    }
    setLoading(true);
    try {
      const payload: {
        names: string;
        note?: string;
        themes?: string[];
        attendance: AttendancePreference;
        alsoFlexible?: boolean;
        alternateDates?: string[];
        alternateTimes?: string[];
      } = { names, attendance: signupAttendance.attendance };
      if (note.trim() !== '') {
        payload.note = note;
      }
      if (selectedThemes.length > 0) {
        payload.themes = selectedThemes;
      }
      if (signupAttendance.alsoFlexible) {
        payload.alsoFlexible = true;
      }
      if (signupAttendance.alternateDates.length > 0) {
        payload.alternateDates = signupAttendance.alternateDates;
      }
      if (signupAttendance.alternateTimes.length > 0) {
        payload.alternateTimes = signupAttendance.alternateTimes;
      }
      const res = await fetch(`${getServerHttpBase()}/api/weekend-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as SignupResponse;
      if (!res.ok || !data.ok) {
        setFeedback(t.ui.weekendHahaSubmitError);
        return;
      }
      applyRoomStatusFromResponse(data, setConfirmedCount, setRoomCapacity, setRoomCount);
      setSessionDate(data.sessionDate);
      if ('sessionHourPst' in data && typeof data.sessionHourPst === 'number') {
        setSessionHourPst(data.sessionHourPst);
      }
      if ('sessionMinutePst' in data && typeof data.sessionMinutePst === 'number') {
        setSessionMinutePst(data.sessionMinutePst);
      }
      const parts: string[] = [];
      if (data.alreadyRegistered.length > 0) {
        parts.push(
          formatString(t.ui.weekendHahaDuplicateMessage, {
            names: data.alreadyRegistered.join('、'),
          }),
        );
      }
      if (data.newlyRegistered.length > 0) {
        const waitlistedNames = data.newlyWaitlisted ?? [];
        const nonWaitlistedNames = data.newlyRegistered.filter(
          (name) => !waitlistedNames.includes(name),
        );

        if (nonWaitlistedNames.length > 0) {
          parts.push(
            formatString(t.ui.weekendHahaSuccessNew, {
              names: nonWaitlistedNames.join('、'),
            }),
          );
        }
        if (waitlistedNames.length > 0) {
          parts.push(
            formatString(t.ui.weekendHahaSuccessWaitlist, {
              names: waitlistedNames.join('、'),
            }),
          );
        }
        if (selectedThemes.length > 0) {
          parts.push(
            formatString(t.ui.weekendHahaSuccessThemes, {
              themes: selectedThemes.join('、'),
            }),
          );
        }
        const resolvedDate = data.sessionDate || sessionDate;
        const resolvedHour =
          'sessionHourPst' in data && typeof data.sessionHourPst === 'number'
            ? data.sessionHourPst
            : sessionHourPst;
        const resolvedMinute =
          'sessionMinutePst' in data && typeof data.sessionMinutePst === 'number'
            ? data.sessionMinutePst
            : sessionMinutePst;
        const sessionTime = resolvedDate
          ? formatSessionTimeLine(resolvedDate, resolvedHour, resolvedMinute)
          : sessionTimeText;
        if (nonWaitlistedNames.length > 0) {
          if (signupAttendance.attendance === 'confirmed') {
            if (signupAttendance.alsoFlexible) {
              const alternates = formatAlternateSummary(
                signupAttendance.alternateDates,
                signupAttendance.alternateTimes,
              );
              if (alternates) {
                parts.push(
                  formatString(t.ui.weekendHahaSuccessConfirmedFlexibleDetails, {
                    sessionTime,
                    alternates,
                  }),
                );
              } else {
                parts.push(formatString(t.ui.weekendHahaSuccessConfirmedFlexible, { sessionTime }));
              }
            } else {
              parts.push(formatString(t.ui.weekendHahaSuccessConfirmed, { sessionTime }));
            }
          } else if (signupAttendance.attendance === 'alternate') {
            parts.push(formatString(t.ui.weekendHahaSuccessAlternate, { sessionTime }));
          } else {
            parts.push(t.ui.weekendHahaSuccessSkip);
          }
        }
      }
      setFeedback(parts.join('\n') || null);
      if (data.newlyRegistered.length > 0) {
        setNames('');
        setNote('');
        resetThemes();
        resetTimePreferences();
      }
    } catch {
      setFeedback(t.ui.weekendHahaSubmitError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
        {t.ui.weekendHahaBlurb}
      </p>

      {sessionDate ? (
        <div
          className={`rounded-xl border-2 border-indigo-200 bg-indigo-50/70 px-4 pt-4 space-y-3 ${
            timeSectionOpen ? 'pb-4' : 'pb-2'
          }`}
        >
          <div>
            <div className="space-y-2.5">
              <p className="text-sm font-semibold text-indigo-900">{t.ui.weekendHahaSessionCardLabel}</p>
              <p className="text-sm text-gray-800 leading-snug">{sessionTimeText}</p>
              {confirmedCount != null && roomCapacity != null && roomCount != null ? (
                <p className="text-sm font-medium text-indigo-800">
                  {formatString(t.ui.weekendHahaRoomStats, {
                    signups: String(confirmedCount),
                    capacity: String(roomCapacity),
                    rooms: String(roomCount),
                  })}
                </p>
              ) : confirmedCount != null ? (
                <p className="text-sm font-medium text-indigo-800">
                  {formatString(t.ui.weekendHahaSignupCount, {
                    signups: String(confirmedCount),
                  })}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setTimeSectionOpen((open) => !open)}
              disabled={loading}
              aria-expanded={timeSectionOpen}
              className="mt-3 inline-flex items-center gap-0.5 text-xs font-medium text-indigo-700 underline underline-offset-2 hover:text-indigo-900 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {timeSectionOpen ? t.ui.weekendHahaTimeMismatchCollapse : t.ui.weekendHahaTimeMismatchToggle}
              <svg
                aria-hidden="true"
                className={`h-3 w-3 shrink-0 transition-transform duration-200 ${timeSectionOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {timeSectionOpen ? (
            <fieldset className="space-y-3 rounded-lg border-2 border-indigo-100 bg-white/90 p-4" disabled={loading}>
            <legend className="text-sm font-medium text-gray-800 px-1">
              {t.ui.weekendHahaAttendanceLabel}
            </legend>

            <div className="space-y-2">
              <div className="space-y-2">
                <label
                  className={`flex items-start gap-3 min-h-[44px] rounded-lg border-2 px-3 py-2.5 cursor-pointer transition-colors ${
                    timePreference === 'available'
                      ? 'border-indigo-500 bg-white shadow-sm'
                      : 'border-gray-200 bg-white hover:border-indigo-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="weekend-time-preference"
                    value="available"
                    checked={timePreference === 'available'}
                    onChange={() => setTimePreferenceMode('available')}
                    disabled={loading}
                    className="mt-1 h-4 w-4 shrink-0 accent-indigo-600"
                  />
                  <span className="text-sm text-gray-800 leading-snug">
                    {t.ui.weekendHahaAttendanceFlexibleTimes}
                  </span>
                </label>

                {timePreference === 'available' ? (
                  <div className="ml-7 border-l-2 border-indigo-100 pl-4">
                    <FlexibleTimesChipsBlock
                      timeChips={sortedTimeChips}
                      dateChips={sortedDateChips}
                      selectedDefaultSession={selectedDefaultSession}
                      selectedDefaultSessionDate={selectedDefaultSessionDate}
                      selectedAlternateDates={selectedAlternateDates}
                      selectedAlternateTimes={selectedAlternateTimes}
                      loading={loading}
                      datesLabel={t.ui.weekendHahaAlternateDatesLabel}
                      timesLabel={t.ui.weekendHahaAlternateTimesLabel}
                      pstHint={alternatePstHint}
                      onToggleDefaultSession={() =>
                        setSelectedDefaultSession((selected) => !selected)
                      }
                      onToggleDefaultSessionDate={() =>
                        setSelectedDefaultSessionDate((selected) => !selected)
                      }
                      onToggleDate={(key) =>
                        setSelectedAlternateDates((prev) => toggleListItem(prev, key))
                      }
                      onToggleTime={(time) =>
                        setSelectedAlternateTimes((prev) => toggleListItem(prev, time))
                      }
                    />
                  </div>
                ) : null}
              </div>

              <label
                className={`flex items-start gap-3 min-h-[44px] rounded-lg border-2 px-3 py-2.5 cursor-pointer transition-colors ${
                  timePreference === 'skip'
                    ? 'border-indigo-500 bg-white shadow-sm'
                    : 'border-gray-200 bg-white hover:border-indigo-200'
                }`}
              >
                <input
                  type="radio"
                  name="weekend-time-preference"
                  value="skip"
                  checked={timePreference === 'skip'}
                  onChange={() => setTimePreferenceMode('skip')}
                  disabled={loading}
                  className="mt-1 h-4 w-4 shrink-0 accent-indigo-600"
                />
                <span className="text-sm text-gray-800 leading-snug">{t.ui.weekendHahaAttendanceSkip}</span>
              </label>
            </div>
          </fieldset>
          ) : null}
        </div>
      ) : null}

      {allThemes.length > 0 ? (
        <fieldset className="space-y-2" disabled={loading}>
          <legend className="text-sm font-medium text-gray-800">
            {t.ui.weekendHahaOptionalThemesLabel}
          </legend>
          <div className="flex flex-wrap gap-2">
            {allThemes.map((theme) => {
              const locked = defaultThemeRequired && theme === defaultTheme;
              return (
                <OptionChip
                  key={theme}
                  label={theme}
                  selected={selectedThemes.includes(theme)}
                  disabled={loading}
                  locked={locked}
                  onToggle={() => toggleTheme(theme)}
                />
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <label className="block text-sm font-medium text-gray-800">
        {t.ui.weekendHahaNamesLabel}
        <input
          type="text"
          value={names}
          onChange={(e) => setNames(e.target.value)}
          placeholder={t.ui.weekendHahaNamesPlaceholder}
          disabled={loading}
          className="mt-1 w-full rounded-lg border-2 border-gray-200 px-3 py-2.5 text-base text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-50"
          autoComplete="name"
        />
      </label>
      <label className="block text-sm font-medium text-gray-800">
        {t.ui.weekendHahaNoteLabel}
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t.ui.weekendHahaNotePlaceholder}
          disabled={loading}
          rows={3}
          className="mt-1 w-full rounded-lg border-2 border-gray-200 px-3 py-2.5 text-base text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-50 resize-y min-h-[88px]"
        />
      </label>
      <button
        type="button"
        onClick={submit}
        disabled={loading}
        className="w-full sm:w-auto min-h-[44px] px-6 py-2.5 rounded-xl font-semibold text-base bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-md hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
      >
        {loading ? t.ui.weekendHahaSubmitting : t.ui.weekendHahaButton}
      </button>
      {feedback ? (
        <p className="text-sm text-gray-800 whitespace-pre-line" role="status">
          {feedback}
        </p>
      ) : null}
    </div>
  );
};
